import type {BusinessTransactionChannel} from '@afriex/core/domain/enums';

import {
  defaultSettlementChannel,
  getSettlementChannelForm,
  settlementChannelFields,
} from '@/screens/Merchants/SettlementAccounts/settlementChannelForms';
import type {SettlementPaymentMethodPayload} from '@/types';
import {
  AfriexCountryCode,
  AfriexCurrencyCode,
  readSettlementAccountField,
} from '@/types';

/**
 * A rejected payload. `field` names the control the message belongs to — an
 * `info` key, or `currencyCode` / `countryCode` / `channel` — so the form can
 * show it on that field rather than only in a toast.
 */
export class SettlementAccountValidationError extends Error {
  readonly field?: string;

  constructor(message: string, field?: string) {
    super(message);
    this.name = 'SettlementAccountValidationError';
    this.field = field;
  }
}

// A function declaration, not a const arrow: TypeScript only narrows control
// flow through a never-returning call when it is declared this way.
function throwErrorMessage(errorMessage: string, field?: string): never {
  throw new SettlementAccountValidationError(errorMessage, field);
}

export class SettlementAccountValidator {
  constructor(private request: Partial<SettlementPaymentMethodPayload>) {}

  /**
   * Walks the channel's registry fields rather than naming them one by one, so
   * a newly registered channel is validated by existing. Values are trimmed and
   * normalized, an empty optional field is left off the payload entirely, and
   * the first failure stops the save — matching `BusinessPoolAccountValidator`.
   */
  private buildInfo(
    channel: BusinessTransactionChannel,
    currencyCode: string,
    countryCode: AfriexCountryCode,
  ): Record<string, string> {
    // Every info shape extends `PaymentMethodInfoBase`, so the country and the
    // currency belong inside `info` as well as on the method itself.
    const info: Record<string, string> = {
      currency: currencyCode,
      country: countryCode,
    };

    for (const field of settlementChannelFields(channel)) {
      const raw = readSettlementAccountField(this.request.info, field.name);
      const value = (field.normalize?.(raw) ?? raw).trim();

      if (!value) {
        if (field.required) {
          throwErrorMessage(`${field.label} is required`, field.name);
        }
        continue;
      }

      const error = field.validate?.(value);
      if (error) {
        throwErrorMessage(error, field.name);
      }

      info[field.name] = value;
    }

    return info;
  }

  value(): SettlementPaymentMethodPayload {
    const channel = this.request.channel ?? defaultSettlementChannel;
    if (!getSettlementChannelForm(channel)) {
      throwErrorMessage(`Unsupported settlement channel ${channel}`, 'channel');
    }

    const {currencyCode, countryCode} = this.request;

    if (!currencyCode) {
      throwErrorMessage('No currency selected', 'currencyCode');
    }
    // The wire type is the wider AfriexAsset; narrow it before asking whether
    // it is one of the currencies we can actually invoice in.
    const settlementCurrency = currencyCode as AfriexCurrencyCode;
    if (!Object.values(AfriexCurrencyCode).includes(settlementCurrency)) {
      throwErrorMessage('Invalid currency code provided', 'currencyCode');
    }
    // Required, not optional: every settlement method is country-scoped.
    if (!countryCode) {
      throwErrorMessage('No country selected', 'countryCode');
    }
    if (!Object.values(AfriexCountryCode).includes(countryCode)) {
      throwErrorMessage('Invalid country code provided', 'countryCode');
    }

    return {
      channel,
      currencyCode,
      countryCode,
      info: this.buildInfo(channel, currencyCode, countryCode),
      meta: {
        flags: {
          isDeactivated: this.request.meta?.flags?.isDeactivated ?? false,
        },
      },
    };
  }
}
